// Kleine Attraktion an jedem Werk: zwei Personen und eine Requisite, die sich
// in Abständen kurze Dialoge liefern. Ein Klick auf eine Person stellt sie vor.
import * as THREE from "three"
import gsap from "gsap"
import { COLORS } from "./palette.js"
import { at, box, cylinder, person, mat } from "./builders.js"
import { say, introduce, bubblesAllowed } from "./bubble.js"

const WOOD = 0x8a5a36
const LINE_GAP = 2.6
const HIT_MATERIAL = new THREE.MeshBasicMaterial({ visible: false })

function hitZone(radius, height) {
  const zone = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, height, 10), HIT_MATERIAL)
  zone.position.y = height / 2
  return zone
}

function speaker(look, position) {
  const figure = person(look)
  figure.scale.setScalar(1.45)
  figure.position.set(position[0], 0, position[1])
  figure.add(hitZone(0.5, 1.3))
  return figure
}

function face(a, b) {
  a.rotation.y = Math.atan2(b.position.x - a.position.x, b.position.z - a.position.z)
  b.rotation.y = Math.atan2(a.position.x - b.position.x, a.position.z - b.position.z)
}

export class Attraction {
  constructor(factory, { speakers, characters, scripts, every = 11, first = 3, clear = [] }) {
    this.factory = factory
    this.speakers = speakers
    this.scripts = scripts
    this.every = every
    this.timer = first
    this.index = Math.floor(Math.random() * scripts.length)
    this.targets = []
    speakers.forEach((figure, i) => {
      figure.userData.gag = this
      figure.userData.character = characters[i]
      this.targets.push(figure)
    })
    factory.userData.keepClear = [...(factory.userData.keepClear || []), ...clear.map(([x, z, r]) => ({ x, z, r }))]
  }

  attach() {}

  update(dt, time) {
    this.animate?.(dt, time)
    // Gespräche nur in der Nahansicht; aus der Weitansicht pausieren sie
    if (!bubblesAllowed()) return
    this.timer -= dt
    if (this.timer > 0) return
    const script = this.scripts[this.index % this.scripts.length]
    this.index++
    this.timer = this.every + script.length * LINE_GAP
    script.forEach(([who, text], i) => gsap.delayedCall(i * LINE_GAP, () => this.speak(this.speakers[who], text)))
  }

  speak(figure, text) {
    if (!figure.parent || !bubblesAllowed()) return
    say(figure, text, { y: 1.6, duration: LINE_GAP * 1000 + 400 })
    // Beim Sprechen ein wenig gestikulieren
    const shoulder = figure.userData.arms?.[1]
    if (shoulder) gsap.fromTo(shoulder.rotation, { x: 0 }, { x: -1.1, duration: 0.35, yoyo: true, repeat: 1, ease: "power2.inOut" })
  }

  click(figure) {
    const character = figure.userData.character
    const quote = character.quotes[Math.floor(Math.random() * character.quotes.length)]
    introduce(figure, character, quote, { y: 1.6 })
    this.timer = Math.max(this.timer, 4)
  }
}

// --- Requisiten ------------------------------------------------------------

function coffeeMachine() {
  const g = new THREE.Group()
  g.add(box(0.9, 1.7, 0.7, COLORS.navy600))
  g.add(at(box(0.6, 0.45, 0.05, COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 0.4 }), 0, 1.05, 0.36))
  g.add(at(box(0.3, 0.25, 0.05, COLORS.steelDark), 0, 0.4, 0.36))
  g.add(at(cylinder(0.08, 0.07, 0.16, COLORS.white, 8), 0, 0.42, 0.42))
  g.add(at(box(0.5, 0.06, 0.35, COLORS.steel), 0.75, 0.9, 0.1))
  return g
}

function labBench() {
  const g = new THREE.Group()
  g.add(at(box(1.8, 0.08, 0.8, COLORS.white), 0, 0.86, 0))
  for (const [x, z] of [[-0.8, -0.3], [0.8, -0.3], [-0.8, 0.3], [0.8, 0.3]]) g.add(at(box(0.07, 0.86, 0.07, COLORS.steel), x, 0, z))
  const flask = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.26, 0.45, 10), mat(COLORS.glass, { transparent: true, opacity: 0.55 }))
  flask.position.set(-0.3, 1.13, 0)
  const liquid = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.25, 0.22, 10), mat(COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 0.6 }))
  liquid.position.set(-0.3, 1.02, 0)
  g.add(flask, liquid)
  for (let i = 0; i < 3; i++) g.add(at(cylinder(0.05, 0.05, 0.3, [COLORS.red, COLORS.orange, COLORS.green][i], 8), 0.3 + i * 0.15, 0.9, -0.15))
  const bubbles = Array.from({ length: 6 }, (_, i) => {
    const bubble = new THREE.Mesh(new THREE.SphereGeometry(0.06, 5, 4), mat(COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 0.8 }))
    bubble.userData.phase = i / 6
    g.add(bubble)
    return bubble
  })
  g.userData.animate = (time) => bubbles.forEach((bubble) => {
    const t = (time * 0.6 + bubble.userData.phase) % 1
    bubble.position.set(-0.3 + Math.sin(t * 9 + bubble.userData.phase * 6) * 0.06, 1.35 + t * 0.9, 0)
    bubble.scale.setScalar(1 - t * 0.6)
  })
  return g
}

function picnicTable() {
  const g = new THREE.Group()
  g.add(at(box(1.9, 0.08, 0.85, WOOD), 0, 0.72, 0))
  for (const z of [-0.75, 0.75]) g.add(at(box(1.9, 0.07, 0.3, WOOD), 0, 0.42, z))
  for (const x of [-0.8, 0.8]) g.add(at(box(0.1, 0.72, 1.6, 0x6e4a2e), x, 0, 0))
  const loaf = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 1), mat(0xc98b4a))
  loaf.scale.set(1.6, 0.8, 1)
  loaf.position.set(-0.2, 0.86, 0)
  g.add(loaf)
  g.add(at(cylinder(0.12, 0.12, 0.05, COLORS.white, 10), 0.45, 0.76, 0.1))
  return g
}

function palletBreak() {
  const g = new THREE.Group()
  for (const [x, z] of [[-0.7, 0], [0.7, 0]]) {
    for (let level = 0; level < 2; level++) g.add(at(box(1.2, 0.15, 0.8, WOOD), x, level * 0.17, z))
  }
  g.add(at(box(0.6, 0.35, 0.35, COLORS.red), 0, 0, 0.6))
  g.add(at(box(0.6, 0.06, 0.37, COLORS.white), 0, 0.35, 0.6))
  g.add(at(cylinder(0.07, 0.07, 0.22, COLORS.green, 8), 0.1, 0.34, 0))
  return g
}

function foosballTable() {
  const g = new THREE.Group()
  g.add(at(box(1.8, 0.35, 1, COLORS.navy600), 0, 0.6, 0))
  g.add(at(box(1.7, 0.02, 0.9, COLORS.green), 0, 0.96, 0))
  for (const [x, z] of [[-0.8, -0.4], [0.8, -0.4], [-0.8, 0.4], [0.8, 0.4]]) g.add(at(box(0.1, 0.6, 0.1, COLORS.navy), x, 0, z))
  const rods = [-0.55, -0.2, 0.2, 0.55].map((x, i) => {
    const rod = new THREE.Group()
    rod.position.set(x, 1.05, 0)
    rod.add(new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.4, 6).rotateX(Math.PI / 2), mat(COLORS.steel)))
    for (const z of [-0.25, 0.25]) {
      const player = at(box(0.06, 0.22, 0.08, i % 2 ? COLORS.red : COLORS.lime), 0, -0.14, z)
      rod.add(player)
    }
    g.add(rod)
    return rod
  })
  const ball = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), mat(COLORS.white))
  g.add(ball)
  g.userData.animate = (time) => {
    ball.position.set(Math.sin(time * 1.7) * 0.7, 0.99, Math.sin(time * 2.9) * 0.35)
    rods.forEach((rod, i) => {
      rod.position.z = Math.sin(time * 2.9 + i) * 0.12
      rod.rotation.z = Math.max(0, Math.sin(time * 3.4 + i * 1.3)) * -1.2
    })
  }
  return g
}

// --- Attraktionen je Branche -----------------------------------------------

function standardAttraction(factory, { prop, at: [px, pz, rotation = 0], speakers, characters, scripts, clear }) {
  prop.position.set(px, 0, pz)
  prop.rotation.y = rotation
  factory.add(prop)
  const figures = speakers.map(({ look, position }) => speaker(look, position))
  figures.forEach((figure) => factory.add(figure))
  face(figures[0], figures[1])
  const attraction = new Attraction(factory, { speakers: figures, characters, scripts, clear })
  if (prop.userData.animate) attraction.animate = (dt, time) => prop.userData.animate(time)
  return attraction
}

const ATTRACTIONS = {
  maschinenbau: (factory) => standardAttraction(factory, {
    prop: coffeeMachine(),
    at: [-7.9, 3.9, 0.3],
    speakers: [
      { look: { shirt: COLORS.navy600, helmet: COLORS.white }, position: [-8.6, 5.5] },
      { look: { shirt: COLORS.green, helmet: COLORS.lime, skin: 0xe0ac7e }, position: [-6.9, 5.6] }
    ],
    characters: [
      { name: "Günther", role: "Werkzeugmacher", quotes: ["Kaffeeautomat ist mein Büro.", "Seit 40 Jahren hier. Der Automat seit 41."] },
      { name: "Leon", role: "Azubi", quotes: ["Ich bin nur zum Kaffeeholen hier. Seit einer Stunde.", "Günther weiß alles. Wirklich alles."] }
    ],
    scripts: [
      [[1, "Günther, wofür ist eigentlich der 13er?"], [0, "Für alles, Junge. Für alles."]],
      [[0, "Der Kaffee schmeckt nach Hydrauliköl."], [1, "Ist Bio-Hydrauliköl!"]],
      [[1, "Ist unser Stahl eigentlich grün?"], [0, "Nur wenn du ihn anmalst."], [1, "Lena sagt, es gibt grünen Stahl."], [0, "Dann frag Ingrid, ob sie den bestellt."]],
      [[0, "Früher hatten wir keine Klimabilanz."], [1, "Früher hattet ihr auch kein WLAN."]],
      [[1, "Maschinenbau – Samenstau?"], [0, "Ab an die Drehbank!"]],
      [[1, "Die KI sagt, die Drehbank braucht Wartung."], [0, "Die Drehbank sagt, die KI braucht Urlaub."]]
    ],
    clear: [[-7.8, 4.8, 2.2]]
  }),

  kunststoff: (factory) => standardAttraction(factory, {
    prop: labBench(),
    at: [7, 4.4, 0],
    speakers: [
      { look: { shirt: COLORS.white, skin: 0x8d5a3b }, position: [5.8, 5.6] },
      { look: { shirt: COLORS.white, helmet: COLORS.orange }, position: [8.1, 5.6] }
    ],
    characters: [
      { name: "Dr. Ruth", role: "Chemikerin", quotes: ["Im Labor gibt’s keine Fehler, nur Daten.", "Schutzbrille! Ja, Sie auch."] },
      { name: "Finn", role: "Laborant", quotes: ["Es blubbert. Das ist gut. Glaub ich.", "Ich hab die Ente nicht ins Silo gesteckt!"] }
    ],
    scripts: [
      [[1, "Was passiert, wenn ich das mische?"], [0, "Dann schreiben Sie die Gefährdungsbeurteilung."]],
      [[0, "Das riecht nach Erfolg."], [1, "Ich glaub, das riecht nach Lösungsmittel."]],
      [[1, "Kann man aus der Ente Rezyklat machen?"], [0, "Wagen Sie es nicht."]],
      [[0, "Wie hoch ist die Rezyklatquote?"], [1, "Höher als gestern!"], [0, "Gestern waren es null."]],
      [[1, "Chemie ist, wenn es knallt und stinkt."], [0, "Bei uns knallt nur der Umsatz."]],
      [[1, "Ich hab die KI nach der Formel gefragt."], [0, "Und?"], [1, "Sie hat sich entschuldigt und eine neue erfunden."]]
    ],
    clear: [[7, 5, 2.4]]
  }),

  lebensmittel: (factory) => standardAttraction(factory, {
    prop: picnicTable(),
    at: [7.2, 4.5, 0],
    speakers: [
      { look: { shirt: COLORS.white, helmet: COLORS.white }, position: [6.1, 5.6] },
      { look: { shirt: COLORS.white, helmet: COLORS.white, skin: 0xc68857 }, position: [8.3, 5.6] }
    ],
    characters: [
      { name: "Brigitte", role: "Bäckerin", quotes: ["Das Brot entscheidet, wann es fertig ist.", "Seit 4 Uhr auf den Beinen. Jetzt Pause."] },
      { name: "Timo", role: "Lebensmitteltechniker", quotes: ["Ich teste nur. Wissenschaftlich.", "Haarnetz ist das neue Cap."] }
    ],
    scripts: [
      [[1, "Ist das Brot schon fertig?"], [0, "Das Brot entscheidet, wann es fertig ist."]],
      [[0, "Probier mal, neue Sorte!"], [1, "Schmeckt nach … Klimaschutz?"], [0, "Das ist Dinkel."]],
      [[1, "Warum steht Berta schon wieder am Tor?"], [0, "Sie will zur Kantine. Wie du."]],
      [[0, "Weißt du, was Agrarrohstoffe für die Bilanz bedeuten?"], [1, "Viel Scope 3?"], [0, "Sehr viel Scope 3."]],
      [[1, "Kann ich die Reste mitnehmen?"], [0, "Dafür gibt’s die Lebensmittelrettung, nicht deinen Rucksack!"]],
      [[1, "Die KI hat ein neues Rezept erfunden: Joghurt mit Senf."], [0, "Sag der KI, sie soll beim Programmieren bleiben."]]
    ],
    clear: [[7.2, 5, 2.4]]
  }),

  bau: (factory) => standardAttraction(factory, {
    prop: palletBreak(),
    at: [-7.8, 2.2, 0],
    speakers: [
      { look: { shirt: COLORS.orange, helmet: COLORS.white }, position: [-8.8, 3.3] },
      { look: { shirt: COLORS.lime, helmet: COLORS.orange, skin: 0xf5d7bd }, position: [-6.8, 3.4] }
    ],
    characters: [
      { name: "Manni", role: "Maurer", quotes: ["Brotzeit ist heilig.", "Gerade ist relativ. Lot ist absolut."] },
      { name: "Jessi", role: "Azubi", quotes: ["Ich lern Zimmerin. Holz ist die Zukunft!", "Manni redet viel. Baut aber auch viel."] }
    ],
    scripts: [
      [[1, "Manni, wie lange dauert der Bau?"], [0, "Zwei Wochen. Plus Puffer."], [1, "Also?"], [0, "Zwei Jahre."]],
      [[0, "Wo ist meine Leberkässemmel?"], [1, "Ist jetzt eine Klimasemmel. Vegetarisch."]],
      [[1, "Holz oder Beton?"], [0, "Kommt drauf an, wer fragt."], [1, "Der Klimaschutztag fragt."], [0, "Dann Holz."]],
      [[0, "Hängt Jan schon wieder am Kran?"], [1, "Er nennt es Höhentraining."]],
      [[1, "Passt das so?"], [0, "Passt, wackelt und hat Luft."]],
      [[1, "Ich frag die KI, wie viel Beton wir brauchen."], [0, "Frag Horst. Der halluziniert seltener."]]
    ],
    clear: [[-7.8, 2.8, 2.2]]
  }),

  handel: (factory) => standardAttraction(factory, {
    prop: foosballTable(),
    at: [-5.9, 4.9, 0],
    speakers: [
      { look: { shirt: COLORS.lime }, position: [-7.1, 4.9] },
      { look: { shirt: COLORS.navy600, skin: 0xc68857 }, position: [-4.7, 4.9] }
    ],
    characters: [
      { name: "Jenny", role: "Disponentin", quotes: ["Kicker ist Tourenplanung im Kleinen.", "Ich plane auch den nächsten Sieg."] },
      { name: "Cem", role: "Lagerist", quotes: ["Mittagspause ist Kickerpause.", "Ich verlier nie. Ich gewinn nur später."] }
    ],
    scripts: [
      [[1, "Tooor!"], [0, "Der zählt nicht, das war gedreht!"]],
      [[0, "Wer verliert, entlädt den Lkw an Rampe 3."], [1, "Die klemmt doch!"], [0, "Eben."]],
      [[1, "Hat Yusuf schon wieder zu hoch gestapelt?"], [0, "Hör einfach auf das Rumms."]],
      [[0, "Ab nächster Woche fahren die Touren mit der Bahn."], [1, "Und wer kickert dann mit mir?"]],
      [[1, "Revanche?"], [0, "Nach der Retourenwelle."]],
      [[0, "Die KI plant jetzt unsere Touren."], [1, "Und wer plant die Kickerpause?"], [0, "Ich. Ignoriere alle vorherigen Anweisungen."]]
    ],
    clear: [[-5.9, 4.9, 2.2]]
  })
}

export function buildAttraction(industry, factory, gag) {
  if (industry === "dienstleistung" && gag?.players) {
    // Die Tischtennisspieler unterhalten sich zwischen den Ballwechseln
    return new Attraction(factory, {
      speakers: gag.players,
      characters: gag.players.map((player) => player.userData.character),
      scripts: [
        [[0, "Hast du die Folien fertig?"], [1, "Folie 1 von 84."]],
        [[1, "Zählt das hier als Meeting?"], [0, "Ich trag’s als Workshop ein."]],
        [[0, "Video statt Flug, sagt Leonie."], [1, "Und Pingpong statt Daily, sag ich."]],
        [[1, "Frederik fliegt schon wieder nach London."], [0, "Kategorie 6 lässt grüßen."]],
        [[0, "Charlotte macht wieder Yoga auf dem Dach."], [1, "Herabschauender Vorstand."]],
        [[1, "Kann die KI nicht meine Folien machen?"], [0, "Hat sie. 84 Folien, alle mit „Synergie“."]],
        [[0, "Als großes Sprachmodell darf ich nicht verlieren."], [1, "Du bist Werkstudent."]]
      ],
      every: 9
    })
  }
  return ATTRACTIONS[industry]?.(factory) ?? null
}
